import { signal } from '@preact/signals';
import { offlinePut } from './offline-storage.js';
import { commitBookToManifest, offlineKey } from './offline-manifest.js';
import { offlineModeStatus } from '../state/offline-mode.js';

export type DownloadScope = 'book' | 'nt' | 'ot' | 'full';

export type DownloadJob = {
  bibleId: string;
  audioStyle: string;
  scope: DownloadScope;
  bookIds: string[];
  totalChapters: number;
  completedChapters: number;
  status: 'downloading' | 'paused' | 'complete' | 'failed';
  currentBook: string;
};

export const activeDownloadJob = signal<DownloadJob | null>(null);

let _paused = false;

const NT_BOOKS = [
  'MAT', 'MRK', 'LUK', 'JHN', 'ACT', 'ROM', '1CO', '2CO', 'GAL', 'EPH',
  'PHP', 'COL', '1TH', '2TH', '1TI', '2TI', 'TIT', 'PHM', 'HEB', 'JAS',
  '1PE', '2PE', '1JN', '2JN', '3JN', 'JUD', 'REV',
];

const OT_BOOKS = [
  'GEN', 'EXO', 'LEV', 'NUM', 'DEU', 'JOS', 'JDG', 'RUT', '1SA', '2SA',
  '1KI', '2KI', '1CH', '2CH', 'EZR', 'NEH', 'EST', 'JOB', 'PSA', 'PRO',
  'ECC', 'SNG', 'ISA', 'JER', 'LAM', 'EZK', 'DAN', 'HOS', 'JOL', 'AMO',
  'OBA', 'JON', 'MIC', 'NAM', 'HAB', 'ZEP', 'HAG', 'ZEC', 'MAL',
];

export async function startDownload(
  bibleId: string,
  audioStyle: string,
  scope: DownloadScope,
  currentBookId?: string,
): Promise<void> {
  const bookIds =
    scope === 'book' && currentBookId
      ? [currentBookId.toUpperCase()]
      : scope === 'nt'
        ? NT_BOOKS
        : scope === 'ot'
          ? OT_BOOKS
          : [...OT_BOOKS, ...NT_BOOKS];

  const { loadBooks } = await import('../audio/biblebrain.js');
  const books = await loadBooks(bibleId);

  let totalChapters = 0;
  for (const bId of bookIds) {
    const book = books.find((b) => b.bookId.toUpperCase() === bId);
    if (book) totalChapters += book.chapters.length;
  }

  _paused = false;
  activeDownloadJob.value = {
    bibleId,
    audioStyle,
    scope,
    bookIds,
    totalChapters,
    completedChapters: 0,
    status: 'downloading',
    currentBook: bookIds[0] ?? '',
  };

  for (const bookId of bookIds) {
    if (_paused) {
      activeDownloadJob.value = { ...activeDownloadJob.value!, status: 'paused' };
      return;
    }

    const book = books.find((b) => b.bookId.toUpperCase() === bookId);
    if (!book) continue;

    activeDownloadJob.value = { ...activeDownloadJob.value!, currentBook: bookId };

    let bookSucceeded = true;

    for (const chapterNum of book.chapters) {
      if (_paused) {
        activeDownloadJob.value = { ...activeDownloadJob.value!, status: 'paused' };
        return;
      }

      const success = await downloadChapterWithRetry(bibleId, bookId, chapterNum, audioStyle, 3);
      if (!success) {
        bookSucceeded = false;
        break;
      }

      activeDownloadJob.value = {
        ...activeDownloadJob.value!,
        completedChapters: (activeDownloadJob.value?.completedChapters ?? 0) + 1,
      };
    }

    if (bookSucceeded) {
      await commitBookToManifest(bibleId, bookId);
    }
  }

  if (activeDownloadJob.value?.status === 'downloading') {
    activeDownloadJob.value = { ...activeDownloadJob.value!, status: 'complete' };
    offlineModeStatus.value = 'offline-partial';
    setTimeout(() => {
      activeDownloadJob.value = null;
    }, 3000);
  }
}

async function downloadChapterWithRetry(
  bibleId: string,
  bookId: string,
  chapter: number,
  audioStyle: string,
  retries: number,
): Promise<boolean> {
  const { loadChapterAudio, loadChapterText } = await import('../audio/biblebrain.js');

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const [audioData, textData] = await Promise.all([
        loadChapterAudio(bookId, chapter, audioStyle as 'plain' | 'dramatized', bibleId),
        loadChapterText(bookId, chapter, bibleId),
      ]);

      if (audioData.audioUrl) {
        const audioResp = await fetch(audioData.audioUrl);
        const audioBuffer = await audioResp.arrayBuffer();
        await offlinePut('chunks', offlineKey(bibleId, bookId, chapter, 'audio'), audioBuffer);
      }

      if (textData.verses.length > 0) {
        const textBuffer = new TextEncoder().encode(JSON.stringify(textData.verses)).buffer as ArrayBuffer;
        await offlinePut('chapters', offlineKey(bibleId, bookId, chapter, 'text'), textBuffer);
      }

      return true;
    } catch {
      if (attempt < retries - 1) {
        await new Promise<void>((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }
  return false;
}

export function pauseDownload(): void {
  _paused = true;
}

export function resumeDownload(): void {
  _paused = false;
  const job = activeDownloadJob.value;
  if (job?.status === 'paused') {
    activeDownloadJob.value = { ...job, status: 'downloading' };
    void startDownload(job.bibleId, job.audioStyle, job.scope, job.currentBook);
  }
}
